import { useCallback, useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { clientAPI } from "@/services/api";
import {
  Bell,
  Check,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Send,
  Webhook,
  X,
} from "lucide-react";
import {
  TailAdminCodeBlock,
  TailAdminEmptyState,
  TailAdminMetricCard,
  TailAdminPageHeader,
  TailAdminPanel,
  TailAdminStatusBadge,
} from "@/components/template/tailadmin/DashboardPrimitives";

const eventLabels = {
  "enrollment.created": "Enrollment Created",
  "enrollment.activated": "Enrollment Activated",
  "enrollment.churned": "Enrollment Churned",
};

const eventTone = {
  "enrollment.created": "primary",
  "enrollment.activated": "success",
  "enrollment.churned": "danger",
};

const formatTime = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleString("en-NG", {
    day: "numeric",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const ClientWebhookLogs = () => {
  const [logs, setLogs] = useState([]);
  const [total, setTotal] = useState(0);
  const [delivered, setDelivered] = useState(0);
  const [failed, setFailed] = useState(0);
  const [eventFilter, setEventFilter] = useState("all");
  const [loading, setLoading] = useState(true);
  const [expandedLog, setExpandedLog] = useState(null);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await clientAPI.getWebhookLogs(50, eventFilter !== "all" ? eventFilter : null);
      const data = res.data || {};
      setLogs(data.items || []);
      setTotal(data.total || 0);
      setDelivered(data.delivered || 0);
      setFailed(data.failed || 0);
    } catch {
      toast.error("Failed to load webhook logs");
      setLogs([]);
      setTotal(0);
      setDelivered(0);
      setFailed(0);
    } finally {
      setLoading(false);
    }
  }, [eventFilter]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const deliveryRate = useMemo(() => {
    if (!total) return "0%";
    return `${Math.round((delivered / total) * 100)}%`;
  }, [delivered, total]);

  return (
    <div className="space-y-6">
      <TailAdminPageHeader
        eyebrow="Operations"
        title="Webhook Logs"
        description="Inspect event delivery, response status, payloads, and integration failures."
        actions={
          <>
            <Select value={eventFilter} onValueChange={setEventFilter}>
              <SelectTrigger className="h-11 w-56">
                <SelectValue placeholder="All Events" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Events</SelectItem>
                <SelectItem value="enrollment.created">Enrollment Created</SelectItem>
                <SelectItem value="enrollment.activated">Enrollment Activated</SelectItem>
                <SelectItem value="enrollment.churned">Enrollment Churned</SelectItem>
              </SelectContent>
            </Select>
            <Button variant="outline" onClick={fetchLogs} disabled={loading}>
              <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
              Refresh
            </Button>
          </>
        }
      />

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <TailAdminMetricCard label="Total events" value={total.toLocaleString()} icon={Send} tone="primary" helper="All webhook attempts" loading={loading} />
        <TailAdminMetricCard label="Delivered" value={delivered.toLocaleString()} icon={Check} tone="success" helper="Successful posts" loading={loading} />
        <TailAdminMetricCard label="Failed" value={failed.toLocaleString()} icon={X} tone="danger" helper="Needs attention" loading={loading} />
        <TailAdminMetricCard label="Delivery rate" value={deliveryRate} icon={Webhook} tone="neutral" helper="Successful ratio" loading={loading} />
      </div>

      <TailAdminPanel title="Event stream" description="Expand a log to inspect the JSON payload and failure message.">
        {loading ? (
          <div className="space-y-3">
            {[0, 1, 2, 3, 4].map((item) => (
              <div key={item} className="h-16 animate-pulse rounded-xl bg-gray-100" />
            ))}
          </div>
        ) : logs.length === 0 ? (
          <TailAdminEmptyState
            icon={Bell}
            title="No webhook events yet"
            description="Events will appear here when enrollment statuses change and Proovam sends webhook notifications."
          />
        ) : (
          <div className="divide-y divide-gray-100">
            {logs.map((log) => {
              const open = expandedLog === log.id;
              const ok = log.status === "delivered";

              return (
                <div key={log.id}>
                  <button
                    className="flex w-full items-center gap-3 px-1 py-4 text-left transition hover:bg-gray-50"
                    onClick={() => setExpandedLog(open ? null : log.id)}
                  >
                    <div className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${ok ? "bg-[#ecfdf3] text-[#039855]" : "bg-[#fef3f2] text-[#d92d20]"}`}>
                      {ok ? <Check className="h-5 w-5" /> : <X className="h-5 w-5" />}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex flex-wrap items-center gap-2">
                        <TailAdminStatusBadge tone={eventTone[log.event_type] || "neutral"}>
                          {eventLabels[log.event_type] || log.event_type || "Event"}
                        </TailAdminStatusBadge>
                        {log.status_code > 0 ? <span className="text-xs font-medium text-gray-400">HTTP {log.status_code}</span> : null}
                      </div>
                      <p className="mt-1 truncate text-xs text-gray-500">{log.webhook_url}</p>
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      <span className="hidden text-xs text-gray-500 sm:block">{formatTime(log.created_at)}</span>
                      {open ? <ChevronUp className="h-4 w-4 text-gray-400" /> : <ChevronDown className="h-4 w-4 text-gray-400" />}
                    </div>
                  </button>

                  {open ? (
                    <div className="pb-4 pl-14 pr-1">
                      <TailAdminCodeBlock className="max-h-56 text-xs">
                        {JSON.stringify(log.payload || {}, null, 2)}
                      </TailAdminCodeBlock>
                      {log.error ? (
                        <div className="mt-3 rounded-xl border border-[#fee4e2] bg-[#fef3f2] p-3 text-sm text-[#b42318]">
                          {log.error}
                        </div>
                      ) : null}
                    </div>
                  ) : null}
                </div>
              );
            })}
          </div>
        )}
      </TailAdminPanel>
    </div>
  );
};

export default ClientWebhookLogs;
